"use client";

import React from "react";

import Image from "next/image";
import Chip from "@/components/global/ui/Chip";
import DecorationLine from "@/components/global/ui/DecorationLine";
import { cn } from "@/lib/utils";
import { PortableText } from "@portabletext/react";
import { richTextComponent } from "@/sanity/sanityPortableText";
import { motion } from "framer-motion";

import quote from "public/global/quote-green.svg";

import { PageMentoringV2Schema } from "@/sanity/pages/PageMentoringV2Template";
import DecorationSquares from "../global/ui/DecorationSquares";

const fadeInUp = (delay?: number) => ({
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-100px" as const },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1], delay },
});

export default function PhilosophySection({ data, className }: { data: PageMentoringV2Schema; className?: string }) {
  return (
    <section className={cn("relative overflow-clip py-[8rem] lg:py-[16rem] bg-[#0A1A1D]", className)}>
      <DecorationSquares
        className="top-[2px]"
        style="green"
      />
      <DecorationLine
        className="hidden top-0 lg:block"
        orientation="vertical"
        style="light"
        position="left"
      />
      <DecorationLine
        className="hidden top-0 lg:block"
        orientation="vertical"
        style="light"
        position="right"
      />

      <div className="container flex flex-col items-center">
        {/* Centered chip + title */}
        <div className="flex flex-col lg:items-center lg:text-center w-[min(100rem,100%)]">
          {data?.philosophyChip && (
            <motion.div {...fadeInUp()}>
              <Chip
                image={data.philosophyChip.icon}
                text={data.philosophyChip.text}
              />
            </motion.div>
          )}

          {data?.philosophyTitle && (
            <motion.div
              {...fadeInUp(0.1)}
              className="richtext richtext-start lg:richtext-center prose-headings:text-white prose-headings:font-ivy-headline-regular prose-headings:text-[5rem] lg:prose-headings:text-[7rem]">
              <PortableText
                value={data.philosophyTitle}
                components={richTextComponent}
              />
            </motion.div>
          )}
        </div>

        {/* About + Callout on the left, Image on the right */}
        <div className="w-[min(160rem,100%)] flex flex-col lg:flex-row gap-[4rem] lg:gap-[8rem] mt-[6rem] lg:h-[83.625rem]">
          {/* Left: About + Callout */}
          <div className="w-[min(79.125rem,100%)] flex flex-col justify-between !h-full ">
            {data?.philosophyAbout && (
              <motion.div
                {...fadeInUp(0.15)}
                className="richtext richtext-start prose-h3:text-[#D9F9B9] prose-h3:text-[3rem] prose-h3:font-ivy prose-h3:mb-[3rem] prose-p:text-white/80">
                <PortableText
                  value={data.philosophyAbout}
                  components={richTextComponent}
                />
              </motion.div>
            )}

            {data?.philosophyCallout && (
              <motion.div
                {...fadeInUp(0.2)}
                className="flex lg:mt-0 p-[1rem] gap-[2rem] bg-[#112628] mt-[4rem]">
                <div className="bg-[#D9F9B9]/10 w-[min(9rem,100%)] shrink-0 flex items-center justify-center flex-col">
                  <Image
                    src={quote}
                    alt=""
                    className="w-[3.5rem]"
                  />
                </div>
                <div className="flex flex-col justify-center h-full py-[1rem]">
                  {data.philosophyCallout.text && (
                    <div className="richtext richtext-start prose-p:text-white prose-p:font-geist prose-p:text-[2.5rem] prose-p:font-[500] prose-p:leading-[140%]">
                      <PortableText
                        value={data.philosophyCallout.text}
                        components={richTextComponent}
                      />
                    </div>
                  )}

                  {data.philosophyCallout.author && (
                    <p className="m-0 text-white/60 mt-[2rem] text-[2.25rem]">— {data.philosophyCallout.author}</p>
                  )}
                </div>
              </motion.div>
            )}
          </div>

          {/* Right: Image */}
          {data?.philosophyImage && (
            <motion.div
              {...fadeInUp(0.2)}
              className="shrink-0 w-[min(75.25rem,100%)]">
              <Image
                className="object-cover w-full h-auto"
                src={data.philosophyImage}
                alt=""
                width={data.philosophyImage.width}
                height={data.philosophyImage.height}
                placeholder={data.philosophyImage.lqip ? "blur" : undefined}
                blurDataURL={data.philosophyImage.lqip}
              />
            </motion.div>
          )}
        </div>
      </div>
    </section>
  );
}
